// =====================================================================================
// INVRT Radar — web console — one site — Visitors tab
//
// The visitor feed for a site. Moved verbatim out of SiteDetailPage.tsx in the C1 split;
// the page shell (pages/SiteDetailPage.tsx) registers it under the tab id "visitors".
//
// The honest bit: person-level identification is a United States capability of the
// underlying data source. Everywhere else a visit resolves at COMPANY level, and this
// panel says so rather than implying a named individual it cannot produce.
//
// C1 additions (story U1): each row opens the shared contact drawer for `vis_<id>`, and
// "Download CSV" is the server's contacts export for this site's identified visitors
// (GET /contacts?format=csv&site_id=…&source=identified), never a client-side re-join.
//
// C7 (mode-aware): the site's mode forks this table. The mode is the site row's
// provider_plan_type in the tenant's own words (employee to b2b, consumer to b2c, exactly
// as routes/sites.ts's tenantPlanType maps it), read from the site row the page shell has
// already loaded under ["site", orgId, siteId] - nothing new is asked of the wire. A b2c
// site leads with COMPANY evidence: the Person and Level columns, the person-level
// promises a consumer-visitor plan never made, are omitted. A b2b site - and a site whose
// mode is not yet set, and a mode read that has not landed - renders today's columns
// exactly. Emphasis only: no row's identified level is ever rewritten.
// =====================================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime, personDisplayName, readString } from "@/lib/format";
import { siteModeOf } from "@/lib/siteMode";
import type { Site, VisitorsResponse } from "@/lib/types";
import { ContactCard } from "@/components/ContactCard";
import { ContactDrawer } from "@/pages/contacts/ContactDrawer";
import { useContactsCsvDownload } from "@/pages/contacts/useContacts";

export function VisitorsPanel({ siteId }: { siteId: string }) {
  const api = useApiClient();
  const { orgId } = useSession();
  const [cursor, setCursor] = useState<string | null>(null);
  const [known, setKnown] = useState<"all" | "true" | "false">("all");
  const [openContactId, setOpenContactId] = useState<string | null>(null);
  const csv = useContactsCsvDownload();

  // C7: the site row the page shell has already loaded, subscribed by the same key so the
  // mode rides the cache (one query, shared with SiteDetailPage). A read that has not yet
  // landed, or has failed, leaves the mode null: today's table, never a broken panel.
  const site = useQuery({
    queryKey: ["site", orgId, siteId],
    queryFn: () => api.get<Site>(`/sites/${siteId}`),
    enabled: Boolean(orgId) && siteId !== "",
  });
  const companyLed = siteModeOf(site.data?.provider_plan_type) === "b2c";

  const visitors = useQuery({
    queryKey: ["visitors", orgId, siteId, cursor, known],
    queryFn: () =>
      api.get<VisitorsResponse>("/visitors", {
        query: {
          site_id: siteId,
          limit: 50,
          ...(cursor ? { cursor } : {}),
          ...(known === "all" ? {} : { known }),
        },
      }),
    enabled: Boolean(orgId),
  });

  if (visitors.isLoading) return <LoadingState label="Loading visitors…" />;
  if (visitors.isError) {
    return <ErrorState error={visitors.error} onRetry={() => void visitors.refetch()} />;
  }
  if (!visitors.data) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Select
          label="Show"
          value={known}
          onChange={(event) => {
            setCursor(null);
            setKnown(event.target.value as typeof known);
          }}
          className="w-56"
        >
          <option value="all">Everyone identified</option>
          <option value="false">New to you only</option>
          <option value="true">Existing customers only</option>
        </Select>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => csv.mutate({ site_id: siteId, source: "identified" })}
            loading={csv.isPending}
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Download CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void visitors.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      {csv.isError ? <ErrorState error={csv.error} /> : null}

      {visitors.data.visitors.length === 0 ? (
        <EmptyState
          title="No identified visitors yet"
          body="Once the pixel is live on your pages, companies looking at your site appear here within minutes of their visit."
        />
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Company</TableHeaderCell>
                {/* C7: a b2c site's table omits the person-level promise columns. */}
                {companyLed ? null : <TableHeaderCell>Person</TableHeaderCell>}
                {companyLed ? null : <TableHeaderCell>Level</TableHeaderCell>}
                <TableHeaderCell>Seen</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Actions</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitors.data.visitors.map((visitor) => {
                const company = readString(visitor.company, "name", "company_name");
                // C5: the row card. This panel resolves the display name with its own
                // honest fallbacks (a company-level row says "Company level", never a
                // person it cannot produce); the card adds the deterministic mark - the
                // person's initials, else the company lettermark - hidden from assistive
                // tech, with the name left in text.
                const isPerson = visitor.identified_level === "person";
                const personName = isPerson ? (personDisplayName(visitor.person) ?? null) : null;
                return (
                  <TableRow key={visitor.id}>
                    <TableCell className="font-medium">
                      {company ?? "\u2014"}
                      {/* C7-F2: a b2c site hides the Level column, but the billing
                          disclosure must survive: a known customer is never billed. */}
                      {companyLed && visitor.is_known_customer ? (
                        <div className="mt-1 font-normal">
                          <Badge tone="neutral">Already yours, not billed</Badge>
                        </div>
                      ) : null}
                    </TableCell>
                    {companyLed ? null : (
                      <>
                        <TableCell className="text-ink-muted">
                          <ContactCard
                            variant="row"
                            identity={{
                              name:
                                (isPerson
                                  ? (personName ?? "Person identified")
                                  : "Company level"),
                              personName,
                              company: { name: company ?? null },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1.5">
                            <Badge tone={visitor.identified_level === "person" ? "success" : "info"}>
                              {visitor.identified_level === "person" ? "Person" : "Company"}
                            </Badge>
                            {visitor.is_known_customer ? (
                              <Badge tone="neutral">Already yours, not billed</Badge>
                            ) : null}
                          </div>
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-ink-muted">
                      {formatDateTime(visitor.received_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Open ${company ?? "this visitor"}`}
                        onClick={() => setOpenContactId(`vis_${visitor.id}`)}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between gap-4">
            <Button
              variant="secondary"
              size="sm"
              disabled={cursor === null}
              onClick={() => setCursor(null)}
            >
              Back to the newest
            </Button>
            <p className="max-w-xl text-center text-xs text-ink-muted">
              Person-level identification is a United States capability. Elsewhere Radar
              names the company, which it states plainly rather than guessing a name.
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={!visitors.data.has_more}
              onClick={() => setCursor(visitors.data.next_cursor)}
            >
              Older visitors
            </Button>
          </div>
        </>
      )}

      {openContactId ? (
        <ContactDrawer contactId={openContactId} onClose={() => setOpenContactId(null)} />
      ) : null}
    </div>
  );
}
